
interface ComboOverviewProps {
  lang: 'en' | 'ja';
}

export default function ComboOverview({ lang }: ComboOverviewProps) {
  const t = {
    en: {
      badge: 'What is Tokyo Night Combo?',
      title: 'Two Iconic Tokyo Experiences,\nOne Unforgettable Night',
      desc: 'We combine the two most authentic Tokyo nightlife experiences into one seamless evening. No stress, no language barriers — just pure fun with a friendly local guide.',
      steps: [
        {
          icon: 'ri-beer-line',
          color: 'amber',
          num: '01',
          label: 'Local Izakaya',
          time: '~2 hrs',
          desc: 'Kick off the night at a real neighborhood izakaya. Cold drinks, delicious small plates, and the warm buzz of Tokyo locals around you.',
        },
        {
          icon: 'ri-arrow-right-line',
          color: 'white',
          num: '',
          label: '',
          time: '',
          desc: '',
          isArrow: true,
        },
        {
          icon: 'ri-mic-fill',
          color: 'pink',
          num: '02',
          label: 'Karaoke Box',
          time: '~2 hrs',
          desc: 'Head to a private karaoke room and sing your heart out. J-Pop, anime hits, English classics — we\'ll guide you through it all.',
        },
        {
          icon: 'ri-arrow-right-line',
          color: 'white',
          num: '',
          label: '',
          time: '',
          desc: '',
          isArrow: true,
        },
        {
          icon: 'ri-heart-fill',
          color: 'rose',
          num: '03',
          label: 'Best Night Ever',
          time: '4–5 hrs total',
          desc: 'Walk away with memories that last a lifetime. This is Tokyo nightlife the way locals actually experience it.',
        },
      ],
      note: '* Food, drinks & room fees paid directly at each venue.',
    },
    ja: {
      badge: 'Tokyo Night Comboとは？',
      title: '東京を代表する2つの体験を\n一夜で楽しむ',
      desc: '居酒屋とカラオケ、東京ナイトライフの定番を一晩でまとめて体験。言葉の壁もストレスもなし。地元の友人と一緒に、本物の東京の夜を。',
      steps: [
        {
          icon: 'ri-beer-line',
          color: 'amber',
          num: '01',
          label: '地元居酒屋',
          time: '約2時間',
          desc: '地元の居酒屋でスタート。冷えたビール、美味しいおつまみ、東京の地元民の活気ある雰囲気を満喫。',
        },
        {
          icon: 'ri-arrow-right-line',
          color: 'white',
          num: '',
          label: '',
          time: '',
          desc: '',
          isArrow: true,
        },
        {
          icon: 'ri-mic-fill',
          color: 'pink',
          num: '02',
          label: 'カラオケボックス',
          time: '約2時間',
          desc: 'プライベートルームで思いっきり歌う。J-Pop・アニメ・英語曲、何でもOK。操作も全部サポート。',
        },
        {
          icon: 'ri-arrow-right-line',
          color: 'white',
          num: '',
          label: '',
          time: '',
          desc: '',
          isArrow: true,
        },
        {
          icon: 'ri-heart-fill',
          color: 'rose',
          num: '03',
          label: '最高の夜の完成',
          time: '合計4〜5時間',
          desc: '一生忘れられない思い出に。これが地元民が実際に楽しむ東京の夜。',
        },
      ],
      note: '※ 飲食代・部屋代は各店舗へ直接お支払いください。',
    },
  }[lang];

  const colorMap: Record<string, string> = {
    amber: 'bg-amber-500/15 border-amber-400/40 text-amber-400',
    pink: 'bg-pink-500/15 border-pink-400/40 text-pink-400',
    rose: 'bg-rose-500/15 border-rose-400/40 text-rose-400',
  };

  return (
    <section className="py-20 px-4 bg-gray-950 text-white">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full mb-5">
            <i className="ri-star-fill text-amber-400 text-sm"></i>
            <span className="text-gray-300 text-sm">{t.badge}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 whitespace-pre-line leading-snug">
            {t.title}
          </h2>
          <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            {t.desc}
          </p>
        </div>

        {/* Flow */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-2">
          {t.steps.map((step, i) => {
            if ((step as { isArrow?: boolean }).isArrow) {
              return (
                <div key={i} className="hidden md:flex w-10 h-10 items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-right-line text-white/30 text-2xl"></i>
                </div>
              );
            }
            return (
              <div
                key={i}
                className="flex-1 bg-white/5 border border-white/10 rounded-2xl p-6 text-center hover:bg-white/8 transition-all duration-300 min-w-0"
              >
                <div className={`w-14 h-14 flex items-center justify-center rounded-full border mx-auto mb-4 ${colorMap[step.color]}`}>
                  <i className={`${step.icon} text-2xl`}></i>
                </div>
                <div className="text-xs font-bold text-white/30 tracking-widest mb-1">{step.num}</div>
                <div className="text-lg font-bold text-white mb-1">{step.label}</div>
                <div className="inline-block text-xs px-3 py-1 bg-white/10 rounded-full text-gray-300 mb-3">{step.time}</div>
                <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Note */}
        <p className="text-center text-gray-500 text-xs mt-8">{t.note}</p>
      </div>
    </section>
  );
}
